/**
 * B10 — touch-gesture decoder for `Scene3DStage`.
 *
 * Pure-math helpers + a small state machine that turns raw
 * `pointerType === "touch"` PointerEvents into AutoCAD-parity gesture
 * intents (pan, pinch-zoom, double-tap, long-press, 2-finger tap).
 *
 * Reference (Autodesk knowledge-base "Touch screen support"):
 *   - 1-finger drag      → pan (no command active) OR pick (during cmd).
 *   - 2-finger pinch     → zoom (pinch=in, spread=out).
 *   - 2-finger drag      → pan during an active command.
 *   - Double-tap         → ZOOM EXTENTS (fit all).
 *   - Long-press (~500 ms) → right-click / context menu.
 *   - 2-finger tap       → Esc.
 *   - 3-finger swipe     → undo / redo (mobile only, deferred).
 *
 * # Why this lives in its own file
 *
 * `stageInteractions.ts` is already 800+ LOC; adding the touch decoder
 * inline pushes it past the 1000-LOC threshold per `CLAUDE.md`. The
 * decoder is also pure-math + a state machine, which makes it trivial
 * to unit-test against synthetic event sequences without spinning up
 * a real DOM.
 */

/** A single live touch finger (mid-gesture). */
export interface TouchPoint {
  /** PointerEvent.pointerId — stable for the lifetime of the touch. */
  readonly pointerId: number;
  /** Cursor position in window-relative CSS pixels at touch-start. */
  readonly startClient: { x: number; y: number };
  /** Most recent cursor position in window-relative CSS pixels. */
  lastClient: { x: number; y: number };
  /** Wall-clock ms when the finger first touched down. */
  readonly startedAtMs: number;
}

/** Long-press threshold (ms) — AutoCAD docs cite "press and hold". */
export const LONG_PRESS_MS = 500;
/** Double-tap interval (ms) — second tap must arrive within this window. */
export const DOUBLE_TAP_MS = 300;
/** Movement (CSS px) past which a single-finger touch is no longer a tap. */
export const TAP_MOVE_THRESHOLD_PX = 6;

/**
 * Internal mutable touch tracker. Lives in a closure inside
 * `installStageInteractions` so a single global pointer doesn't leak
 * across multiple stage instances (rare, but tests mount/unmount the
 * stage repeatedly).
 *
 * Exposed via the helpers below as a pure value type so unit tests can
 * drive it without DOM events.
 */
export interface TouchState {
  /** Active fingers, keyed by pointerId. */
  readonly points: Map<number, TouchPoint>;
  /** Wall-clock timestamp of the last single-finger tap commit (ms). */
  lastTapAtMs: number;
  /** Cursor position (window-relative CSS px) of the last tap commit. */
  lastTapClient: { x: number; y: number } | null;
  /** Long-press timer handle (browser timer id) — null when idle. */
  longPressTimer: number | null;
  /**
   * Mid-gesture pinch baseline. Captured on the second finger's
   * pointerdown; reused on every subsequent pointermove to derive a
   * zoom factor. `null` between gestures.
   */
  pinch: {
    initialDistancePx: number;
    initialMidpointClient: { x: number; y: number };
    lastDistancePx: number;
    lastMidpointClient: { x: number; y: number };
  } | null;
  /**
   * Whether the active 2-finger gesture is "mostly pan" (midpoint
   * moves) vs "mostly pinch" (distance changes). Chosen lazily on the
   * first sufficiently-large delta and remembered so a pinch doesn't
   * accidentally pan as the fingers drift sideways.
   */
  twoFingerMode: "pinch" | "pan" | null;
}

/** Build a fresh, empty touch state. */
export function createTouchState(): TouchState {
  return {
    points: new Map(),
    lastTapAtMs: 0,
    lastTapClient: null,
    longPressTimer: null,
    pinch: null,
    twoFingerMode: null,
  };
}

/**
 * Euclidean distance between two screen points. Returned in CSS pixels.
 */
export function distance(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}

/** Midpoint of two screen points. */
export function midpoint(
  a: { x: number; y: number },
  b: { x: number; y: number },
): { x: number; y: number } {
  return { x: (a.x + b.x) * 0.5, y: (a.y + b.y) * 0.5 };
}

/**
 * Decide whether a 2-finger move should be treated as a pinch (zoom)
 * vs a pan. Returns the dominant axis, or `null` if neither delta
 * crosses the disambiguation threshold yet.
 *
 * Rule (AutoCAD parity matches Apple "iPad touch" precedent):
 *   - distance change > 8 CSS px AND > midpoint move →  pinch
 *   - midpoint move > 8 CSS px   AND > distance change → pan
 *   - else: undecided (caller defers).
 *
 * Pure function — exported for unit tests.
 */
export function decideTwoFingerMode(
  initialDistancePx: number,
  initialMidpoint: { x: number; y: number },
  currentDistancePx: number,
  currentMidpoint: { x: number; y: number },
  thresholdPx = 8,
): "pinch" | "pan" | null {
  const distDelta = Math.abs(currentDistancePx - initialDistancePx);
  const midDelta = distance(initialMidpoint, currentMidpoint);
  if (distDelta < thresholdPx && midDelta < thresholdPx) return null;
  return distDelta >= midDelta ? "pinch" : "pan";
}

/**
 * Compute the zoom factor for a pinch gesture. The factor is the ratio
 * of current finger separation to the baseline at gesture start —
 * spread = factor > 1 (zoom in), pinch = factor < 1 (zoom out).
 *
 * Mirrors `wheelDeltaToZoomFactor` for the wheel path; both feed into
 * `zoomAroundCursor`.
 */
export function pinchZoomFactor(
  initialDistancePx: number,
  currentDistancePx: number,
): number {
  if (!Number.isFinite(initialDistancePx) || initialDistancePx <= 0) return 1;
  if (!Number.isFinite(currentDistancePx) || currentDistancePx <= 0) return 1;
  return currentDistancePx / initialDistancePx;
}

/**
 * Returns true if the second tap of a candidate double-tap pair is
 * within the time + space windows of the first. AutoCAD parity defers
 * to the OS-default double-tap interval (~300 ms) and a small spatial
 * tolerance so a finger landing slightly off the first hit still
 * registers as a double-tap.
 */
export function isDoubleTap(
  firstAtMs: number,
  firstClient: { x: number; y: number } | null,
  secondAtMs: number,
  secondClient: { x: number; y: number },
  intervalMs = DOUBLE_TAP_MS,
  spatialTolerancePx = 16,
): boolean {
  if (!firstClient) return false;
  const dt = secondAtMs - firstAtMs;
  if (dt < 0 || dt > intervalMs) return false;
  return distance(firstClient, secondClient) <= spatialTolerancePx;
}
